import { create } from 'zustand';
import type { FileClassification } from '@/components/assistant/fileClassification';
import type { GuideId } from '@/lib/guides/engine/types';
import type { QuoteResultAction } from '@/lib/quoteResultAction';

/**
 * Mirrors the orchestrator's `PortalConfirmationOutcome` (#2104). Carried on an
 * `action_confirmed` turn; `'unknown'` means the write may or may not have been
 * applied and can never be re-run.
 */
export type AssistantActionOutcome = 'ok' | 'failed' | 'unknown';

/**
 * How a pending confirmation is rendered (#2180): an i18n key in the
 * `assistant` namespace plus primitive params, resolved by the client. Sent by
 * the harness path only; a legacy card has none and renders `description`.
 * If the key is missing from the bundle the card renders `description` too —
 * a raw key never reaches the screen.
 */
export interface ConfirmationPresenter {
  key: string;
  params: Record<string, string | number | boolean>;
}

/**
 * Why a preview was refused or a confirmed action failed (#2587): a stable
 * code rendered from `failureReasons.<code>` in the `assistant` namespace,
 * plus primitive params. A code the bundle does not know renders a generic
 * localised line — never the code, and never the English `content`.
 */
export interface AssistantFailureReason {
  code: string;
  params: Record<string, string | number | boolean>;
}

/** A file the user attached to a Buddy turn. Metadata only — never the bytes. */
export type AssistantAttachmentKind = 'image' | 'pdf' | 'text' | 'spreadsheet';

export interface AssistantAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  kind: AssistantAttachmentKind;
}

export interface AssistantMessage {
  id: string;
  conversationId?: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  type?: 'message' | 'pending_confirmation' | 'action_confirmed' | 'action_cancelled' | 'guide';
  actionId?: string;
  /**
   * Set on an `action_confirmed` turn, and — as `'unknown'` only — on an
   * `action_cancelled` turn whose earlier confirmed attempt may still commit
   * (#2597). Carried by the HTTP reply, the `assistant:response` WebSocket
   * event and the persisted transcript (#2186), so a socket-only turn and a
   * reloaded one both keep it.
   */
  outcome?: AssistantActionOutcome;
  toolName?: string;
  params?: Record<string, unknown>;
  /**
   * The card's presenter: on `pending_confirmation` turns, and (#2587) on the
   * settled `action_confirmed` / `action_cancelled` turn it ended in, whose
   * line is rendered from it in the user's language.
   */
  presenter?: ConfirmationPresenter;
  /** A preview refusal's or a failed attempt's reason (#2587). */
  failure?: AssistantFailureReason;
  /**
   * Additive (#2821), `action_confirmed` of a quote acceptance: the receipt
   * time that was sent (the pinned, staff-confirmed one). Read alike from the
   * HTTP reply, the socket event and the persisted transcript.
   */
  pinnedReceivedAt?: string;
  /**
   * Additive (#2821): this settled reply replayed an action completed
   * EARLIER (with `pinnedReceivedAt` as recorded then), not a fresh success.
   */
  alreadyCompleted?: boolean;
  quoteResult?: QuoteResultAction;
  /**
   * Read-only file scan (#3996). One entry per attached file on this turn.
   * Filled from the HTTP reply, the socket event and
   * `metadata.fileClassifications` when the transcript is reloaded.
   */
  fileClassifications?: FileClassification[];
  /**
   * Client-side only: an application-authored notice (a failed /confirm or
   * /cancel, a failed send) that may interpolate data such as a card's
   * description. Rendered as plain text, never as Markdown (#2587 review).
   */
  plain?: boolean;
  /**
   * Client-side guide narration (#3896). `guideTextKey` is a key in the
   * `guides` namespace (a step, or the guide's done text). `guideNoticeKey`
   * is a key in the `assistant` namespace (narrow screen, missing anchor,
   * stopped, finished). `guideParams` interpolates either line. Rendered as
   * plain text; a missing key falls back to a generic line, never the raw key.
   */
  guideTextKey?: string;
  guideNoticeKey?: string;
  guideParams?: Record<string, string | number | boolean>;
  metadata?: Record<string, unknown>;
  /**
   * Files on a user turn (#3995). Set on the optimistic send and restored from
   * the history row's `attachments` array (older rows nest the same list in
   * `content`). Names and ids only — the bytes never live here.
   */
  attachments?: AssistantAttachment[];
}

export interface PageContext {
  route: string;
  entityType?: string;
  entityId?: string;
  entityName?: string;
  /** Latest authorized read on explicit quote return; navigation hints, never approval evidence. */
  entityStatus?: string;
  entityUpdatedAt?: string;
  /** Set when an insight is opened into the conversation (issue #1090). */
  insightId?: string;
  /** Insight reference (rule version) carried alongside insightId for traceability. */
  insightRef?: string;
  /** Set when an operational exception case is opened into the chat (issue #1097). */
  exceptionId?: string;
  /** Case version at open time, carried alongside exceptionId for traceability. */
  exceptionRef?: string;
  /**
   * Active guide step (#3896). Filled when the chat request is built;
   * `stepId` is at most 100 characters on the wire.
   */
  guide?: { guideId: GuideId; stepId: string };
}

/** Which surface the panel shows: the chat or the proactive insights inbox. */
export type AssistantView = 'chat' | 'insights';

interface PendingConfirmation {
  actionId: string;
  toolName: string;
  description: string;
  params: Record<string, unknown>;
  presenter?: ConfirmationPresenter;
}

function persistSettledIds(
  byConversation: Record<string, string[]>,
  conversationId: string | null,
  settledActionIds: string[],
): Record<string, string[]> {
  if (!conversationId) return byConversation;
  return { ...byConversation, [conversationId]: settledActionIds };
}

/**
 * Keep a live pending_confirmation row that a stale snapshot omitted, so the
 * Confirm card stays attached to a renderable transcript row (#2960).
 */
export function mergeHistoryMessages(
  incoming: readonly AssistantMessage[],
  current: readonly AssistantMessage[],
  pending: { actionId: string } | null,
): AssistantMessage[] {
  if (!pending) return [...incoming];
  if (
    incoming.some((m) => m.type === 'pending_confirmation' && m.actionId === pending.actionId)
  ) {
    return [...incoming];
  }
  const live = current.find(
    (m) => m.type === 'pending_confirmation' && m.actionId === pending.actionId,
  );
  return live ? [...incoming, live] : [...incoming];
}

/**
 * A confirm the server refused while KEEPING its plan (#2821): the card is put
 * back with what the user had entered, plus why, so they can correct it and
 * confirm again. `retryable` is the harness's "nothing ran, send it again".
 */
export interface ConfirmationRetry {
  actionId: string;
  reason:
    | 'quote_receipt_time_required'
    | 'quote_receipt_time_invalid'
    | 'quote_receipt_time_future'
    | 'retryable';
  /** The receipt time that confirm carried (or the server's pinned one), restored into the card. */
  approvalReceivedAt?: string;
  /**
   * RECONCILING: an attempt may already exist on the server, which will
   * re-send its pinned time whatever is entered now, so the card shows that
   * time read-only and only offers to confirm again. Unset = EDITABLE: the
   * refusal happened before anything could be recorded.
   */
  locked?: boolean;
}

interface AssistantState {
  isOpen: boolean;
  view: AssistantView;
  conversationId: string | null;
  messages: AssistantMessage[];
  isLoading: boolean;
  currentContext: PageContext | null;
  pendingConfirmation: PendingConfirmation | null;
  confirmationRetry: ConfirmationRetry | null;
  /**
   * Settled action IDs of the CURRENT conversation (#2821, #2960). Settlement
   * is final for that conversation: a later pending event, 409 or 503 for one
   * of these never re-arms its card. Other conversations keep their own sets.
   */
  settledActionIds: string[];
  /**
   * Settled action IDs keyed by conversation (#2960). Survives a switch so a
   * stale transcript cannot re-arm a card this conversation already settled.
   */
  settledActionIdsByConversation: Record<string, string[]>;
  /**
   * Bumped at the start of every history load and on `clearConversation`.
   * A GET that finishes with a stale generation is discarded (#2960).
   */
  historyLoadGeneration: number;

  setOpen: (open: boolean) => void;
  toggleOpen: () => void;
  setView: (view: AssistantView) => void;
  setConversationId: (id: string | null) => void;
  addMessage: (message: AssistantMessage) => void;
  upsertAssistantMessage: (message: AssistantMessage) => void;
  setMessages: (messages: AssistantMessage[]) => void;
  setLoading: (loading: boolean) => void;
  setCurrentContext: (context: PageContext | null) => void;
  setPendingConfirmation: (confirmation: PendingConfirmation | null) => void;
  setConfirmationRetry: (retry: ConfirmationRetry | null) => void;
  /** Record a settled action and retire its card and correction state. */
  markSettled: (actionId: string) => void;
  /**
   * THE way a pending card is armed (#2821) — HTTP chat reply, socket event,
   * transcript restore and a kept-plan confirm refusal all go through it. An
   * action already known to be settled is never re-armed. Returns whether
   * the card was armed.
   */
  armPendingConfirmation: (confirmation: PendingConfirmation) => boolean;
  /**
   * Merge settlement read from a transcript into the CURRENT conversation's
   * set (union): a stale transcript can add settled ids, never remove them.
   */
  mergeSettled: (actionIds: readonly string[]) => void;
  /** Start a history GET; the returned generation identifies this load. */
  beginHistoryLoad: () => number;
  /**
   * Replace the transcript from a history snapshot, keeping a live pending
   * row the snapshot omitted so its Confirm card remains renderable (#2960).
   */
  applyHistoryMessages: (incoming: AssistantMessage[]) => void;
  /** Open an insight into the chat: attach its id + ref to context and switch to chat. */
  openInsight: (insightId: string, insightRef: string) => void;
  /**
   * Open an operational exception case into the chat: attach its id + version to
   * context, switch to chat, and open the panel (the queue lives on its own page,
   * so unlike an insight the panel may be closed). Clears any insight reference so
   * the two hand-offs never leak into one conversation turn.
   */
  openException: (exceptionId: string, exceptionRef: string) => void;
  clearConversation: () => void;
}

export const useAssistantStore = create<AssistantState>()((set) => ({
  isOpen: false,
  view: 'chat',
  conversationId: null,
  messages: [],
  isLoading: false,
  currentContext: null,
  pendingConfirmation: null,
  confirmationRetry: null,
  settledActionIds: [],
  settledActionIdsByConversation: {},
  historyLoadGeneration: 0,

  setOpen: (open) => set({ isOpen: open }),
  toggleOpen: () => set((state) => ({ isOpen: !state.isOpen })),
  setView: (view) => set({ view }),
  setConversationId: (id) =>
    set((state) => {
      if (state.conversationId === id) return {};
      const byConversation = persistSettledIds(
        state.settledActionIdsByConversation,
        state.conversationId,
        state.settledActionIds,
      );
      const settledActionIds = id
        ? [
            ...new Set([
              ...(byConversation[id] ?? []),
              ...(state.conversationId ? [] : state.settledActionIds),
            ]),
          ]
        : [];
      return {
        conversationId: id,
        settledActionIds,
        settledActionIdsByConversation: persistSettledIds(byConversation, id, settledActionIds),
      };
    }),
  addMessage: (message) =>
    set((state) =>
      state.messages.some((m) => m.id === message.id)
        ? state
        : { messages: [...state.messages, message] },
    ),
  upsertAssistantMessage: (message) =>
    set((state) => {
      const idx = state.messages.findIndex((m) => m.id === message.id);
      if (idx >= 0) {
        const next = state.messages.slice();
        next[idx] = { ...next[idx], ...message };
        return { messages: next };
      }
      return { messages: [...state.messages, message] };
    }),
  setMessages: (messages) => set({ messages }),
  setLoading: (loading) => set({ isLoading: loading }),
  setCurrentContext: (context) => set({ currentContext: context }),
  setPendingConfirmation: (confirmation) =>
    set({ pendingConfirmation: confirmation }),
  setConfirmationRetry: (retry) => set({ confirmationRetry: retry }),
  armPendingConfirmation: (confirmation) => {
    let armed = false;
    set((state) => {
      if (state.settledActionIds.includes(confirmation.actionId)) return {};
      armed = true;
      return { pendingConfirmation: confirmation };
    });
    return armed;
  },
  mergeSettled: (actionIds) =>
    set((state) => {
      const merged = [...new Set([...state.settledActionIds, ...actionIds])];
      return {
        settledActionIds: merged,
        settledActionIdsByConversation: persistSettledIds(
          state.settledActionIdsByConversation,
          state.conversationId,
          merged,
        ),
        pendingConfirmation:
          state.pendingConfirmation && merged.includes(state.pendingConfirmation.actionId)
            ? null
            : state.pendingConfirmation,
        confirmationRetry:
          state.confirmationRetry && merged.includes(state.confirmationRetry.actionId)
            ? null
            : state.confirmationRetry,
      };
    }),
  markSettled: (actionId) =>
    set((state) => {
      const settledActionIds = state.settledActionIds.includes(actionId)
        ? state.settledActionIds
        : [...state.settledActionIds, actionId];
      return {
        settledActionIds,
        settledActionIdsByConversation: persistSettledIds(
          state.settledActionIdsByConversation,
          state.conversationId,
          settledActionIds,
        ),
        pendingConfirmation:
          state.pendingConfirmation?.actionId === actionId ? null : state.pendingConfirmation,
        confirmationRetry:
          state.confirmationRetry?.actionId === actionId ? null : state.confirmationRetry,
      };
    }),
  beginHistoryLoad: () => {
    let generation = 0;
    set((state) => {
      generation = state.historyLoadGeneration + 1;
      return { historyLoadGeneration: generation };
    });
    return generation;
  },
  applyHistoryMessages: (incoming) =>
    set((state) => ({
      messages: mergeHistoryMessages(incoming, state.messages, state.pendingConfirmation),
    })),
  openInsight: (insightId, insightRef) =>
    set((state) => ({
      view: 'chat',
      currentContext: {
        ...(state.currentContext ?? { route: '' }),
        insightId,
        insightRef,
      },
    })),
  openException: (exceptionId, exceptionRef) =>
    set((state) => ({
      isOpen: true,
      view: 'chat',
      currentContext: {
        ...(state.currentContext ?? { route: '' }),
        insightId: undefined,
        insightRef: undefined,
        exceptionId,
        exceptionRef,
      },
    })),
  clearConversation: () =>
    set((state) => ({
      conversationId: null,
      messages: [],
      pendingConfirmation: null,
      confirmationRetry: null,
      settledActionIds: [],
      settledActionIdsByConversation: persistSettledIds(
        state.settledActionIdsByConversation,
        state.conversationId,
        state.settledActionIds,
      ),
      historyLoadGeneration: state.historyLoadGeneration + 1,
    })),
}));
